/**
 * Утилиты для резолва колонки `reference_attribute` с произвольной цепочкой
 * (path) колонок справочников. См. ReferenceAttributeColumnConfig в
 * @lens/shared/schemas/views.
 *
 * Все функции — синхронные и работают по уже загруженным данным
 * (dictionaryEntryValues / dictionaryColumnsById / dictionaryOptionsById).
 * Догрузка справочников выполняется отдельно в SpacePage useEffect'ах.
 */
import type { ReferenceAttributeColumnConfig } from '@lens/shared';

/**
 * Lite-тип DictionaryColumn — только поля, которые реально используются при
 * walk/render, чтобы было совместимо с минимальным `dictionaryColumnsById`,
 * хранящимся в SpacePage (без id/name/dictionaryId).
 */
export interface DictColumnLite {
  slug: string;
  columnType: string;
  config: unknown;
}

export interface RefAttrCtx {
  /** entryId → values (полные values записи справочника). */
  dictionaryEntryValues: Record<string, Record<string, unknown>>;
  /** dictionaryId → колонки справочника. */
  dictionaryColumnsById: Record<string, DictColumnLite[]>;
  /** dictionaryId → опции (entryId → display label) для финального резолва reference-листа. */
  dictionaryOptionsById: Record<string, Array<{ value: string; label: string }>>;
  /** Глобальный fallback для display label'а entry. */
  resolvedRefs: Record<string, string>;
}

/** Backward-compat: вернуть path из конфига, fallback на legacy attributeSlug. */
export function getRefAttrPath(col: ReferenceAttributeColumnConfig): string[] {
  if (Array.isArray(col.path) && col.path.length > 0) return col.path;
  if (typeof col.attributeSlug === 'string' && col.attributeSlug.length > 0) {
    return [col.attributeSlug];
  }
  return [];
}

/**
 * Собирает все промежуточные dictionaryId, которые надо загрузить для резолва
 * данной цепочки. Возвращает массив [rootDictId, ...nestedDictIds].
 */
export function collectRefAttrDictionaryIds(
  rootDictionaryId: string,
  path: string[],
  dictionaryColumnsById: Record<string, DictColumnLite[]>,
): string[] {
  const ids: string[] = [rootDictionaryId];
  let currentDictId: string | undefined = rootDictionaryId;
  for (let i = 0; i < path.length; i += 1) {
    if (!currentDictId) break;
    const slug = path[i];
    if (!slug) break;
    const cols: DictColumnLite[] | undefined = dictionaryColumnsById[currentDictId];
    if (!cols) break;
    const col: DictColumnLite | undefined = cols.find((c: DictColumnLite) => c.slug === slug);
    if (!col) break;
    if (col.columnType !== 'reference') break;
    const nextDictId: string | undefined =
      (col.config as { dictionaryId?: string } | null)?.dictionaryId;
    if (!nextDictId) break;
    if (!ids.includes(nextDictId)) ids.push(nextDictId);
    currentDictId = nextDictId;
  }
  return ids;
}

/**
 * Возвращает финальный DictionaryColumn (последний слой) для данной цепочки —
 * нужен для понимания типа листа и для синтетического FieldDefinition фильтра/группировки.
 */
export function getFinalRefAttrColumn(
  rootDictionaryId: string,
  path: string[],
  dictionaryColumnsById: Record<string, DictColumnLite[]>,
): DictColumnLite | undefined {
  let currentDictId: string | undefined = rootDictionaryId;
  let lastCol: DictColumnLite | undefined;
  for (let i = 0; i < path.length; i += 1) {
    if (!currentDictId) return undefined;
    const slug = path[i];
    if (!slug) return lastCol;
    const cols: DictColumnLite[] | undefined = dictionaryColumnsById[currentDictId];
    if (!cols) return undefined;
    const col: DictColumnLite | undefined = cols.find((c: DictColumnLite) => c.slug === slug);
    if (!col) return undefined;
    lastCol = col;
    if (i === path.length - 1) return col;
    if (col.columnType !== 'reference') return col;
    currentDictId = (col.config as { dictionaryId?: string } | null)?.dictionaryId;
  }
  return lastCol;
}

/**
 * Walk по цепочке: на каждом шаге читает entry.values[slug]. Если значение —
 * массив (multi-reference на промежуточном шаге), разворачивает в плоский
 * список. На последнем шаге возвращает накопленные «сырые» значения листа.
 */
export function walkRefAttrPath(
  entryIds: string[],
  path: string[],
  rootDictionaryId: string,
  ctx: RefAttrCtx,
): unknown[] {
  if (path.length === 0) return [];
  let currentIds = entryIds.filter((id) => typeof id === 'string' && id.length > 0);
  let currentDictId: string | undefined = rootDictionaryId;

  for (let depth = 0; depth < path.length; depth += 1) {
    const slug = path[depth];
    if (!slug) return [];
    if (!currentDictId) return [];
    const cols: DictColumnLite[] | undefined = ctx.dictionaryColumnsById[currentDictId];
    const col: DictColumnLite | undefined = cols?.find((c: DictColumnLite) => c.slug === slug);
    const isLast = depth === path.length - 1;

    const collected: unknown[] = [];
    for (const id of currentIds) {
      const values = ctx.dictionaryEntryValues[id];
      if (!values) continue;
      const raw = values[slug];
      if (raw === null || raw === undefined || raw === '') continue;
      if (Array.isArray(raw)) {
        for (const v of raw) collected.push(v);
      } else {
        collected.push(raw);
      }
    }

    if (isLast) return collected;

    // Промежуточный шаг — должен быть reference-колонкой; собранные значения = entryIds.
    if (!col || col.columnType !== 'reference') return [];
    const nextDictId: string | undefined =
      (col.config as { dictionaryId?: string } | null)?.dictionaryId;
    if (!nextDictId) return [];
    currentIds = collected.filter((v): v is string => typeof v === 'string' && v.length > 0);
    if (currentIds.length === 0) return [];
    currentDictId = nextDictId;
  }
  return [];
}

/** Форматирует одно raw-значение листа в строку с учётом типа финальной колонки. */
function formatLeafValue(raw: unknown, finalCol: DictColumnLite | undefined, ctx: RefAttrCtx): string {
  if (raw === null || raw === undefined || raw === '') return '';
  if (!finalCol) return String(raw);
  if (finalCol.columnType === 'select' || finalCol.columnType === 'multi_select') {
    const opts = ((finalCol.config as { options?: Array<{ value: string; label: string }> } | null)?.options) ?? [];
    return opts.find((o) => o.value === raw)?.label ?? String(raw);
  }
  if (finalCol.columnType === 'boolean') {
    return raw ? 'Да' : 'Нет';
  }
  if (finalCol.columnType === 'reference') {
    const nestedDictId = (finalCol.config as { dictionaryId?: string } | null)?.dictionaryId;
    const nestedOpts = nestedDictId ? ctx.dictionaryOptionsById[nestedDictId] : undefined;
    const labelById = new Map(nestedOpts?.map((o) => [o.value, o.label]) ?? []);
    const id = String(raw);
    return labelById.get(id) ?? ctx.resolvedRefs[id] ?? id;
  }
  return String(raw);
}

/**
 * Финальная строка для рендера в ячейке таблицы. Объединяет multi-листья через `, `,
 * группы по разным root entry id — через `; `.
 */
export function renderRefAttrPath(
  entryIds: string[],
  path: string[],
  rootDictionaryId: string,
  ctx: RefAttrCtx,
): string {
  const finalCol = getFinalRefAttrColumn(rootDictionaryId, path, ctx.dictionaryColumnsById);
  const groups: string[] = [];
  for (const id of entryIds.filter((v) => typeof v === 'string' && v.length > 0)) {
    const leaves = walkRefAttrPath([id], path, rootDictionaryId, ctx);
    if (leaves.length === 0) continue;
    const parts = leaves
      .map((leaf) => formatLeafValue(leaf, finalCol, ctx))
      .filter((s) => s.length > 0);
    if (parts.length === 0) continue;
    groups.push(parts.join(', '));
  }
  return groups.join('; ');
}
